import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowRight, MoreVertical, Pencil, Trash2, X } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";

interface ListOptionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listName: string;
  listId: string;
  groupId: string | null;
  groups: { id: string; name: string }[];
  onRename: () => void;
  onDelete: () => void;
  onMoveToGroup: (groupId: string | null) => void;
}

export function ListOptionsDialog(props: ListOptionsDialogProps) {
  const { t } = useI18n();
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-[320px] p-0">
      <DialogHeader>
        <div class="flex items-center gap-2 pl-2">
          <MoreVertical class="h-4 w-4 opacity-50" />
          <DialogTitle class="text-sm font-bold truncate max-w-[200px]">
            {props.listName}
          </DialogTitle>
        </div>
      </DialogHeader>

      <div class="p-2 space-y-1">
        <button
          type="button"
          onClick={() => {
            props.onRename();
            props.onOpenChange(false);
          }}
          class="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-accent transition-colors text-left group cursor-pointer"
        >
          <div class="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
            <Pencil class="h-4 w-4" />
          </div>
          <div class="font-bold text-sm">{t(msg`Rename`)}</div>
        </button>

        <button
          type="button"
          onClick={() => {
            props.onDelete();
            props.onOpenChange(false);
          }}
          class="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-destructive/10 transition-colors text-left group text-destructive cursor-pointer"
        >
          <div class="h-9 w-9 rounded-full bg-destructive/10 flex items-center justify-center text-destructive group-hover:scale-110 transition-transform">
            <Trash2 class="h-4 w-4" />
          </div>
          <div class="font-bold text-sm">{t(msg`Delete`)}</div>
        </button>

        <div class="h-px bg-border/10 my-2 mx-2" />

        <div class="px-3 py-1 text-[10px] font-bold uppercase tracking-wider opacity-40">
          {t(msg`Move to group`)}
        </div>

        <Show when={props.groupId}>
          <button
            type="button"
            onClick={() => {
              props.onMoveToGroup(null);
              props.onOpenChange(false);
            }}
            class="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-accent transition-colors text-left group cursor-pointer"
          >
            <div class="h-9 w-9 rounded-full bg-muted flex items-center justify-center text-muted-foreground group-hover:scale-110 transition-transform">
              <X class="h-4 w-4" />
            </div>
            <div class="text-sm font-medium">{t(msg`Remove from group`)}</div>
          </button>
        </Show>

        <For each={props.groups}>
          {(group) => (
            <Show when={group.id !== props.groupId}>
              <button
                type="button"
                onClick={() => {
                  props.onMoveToGroup(group.id);
                  props.onOpenChange(false);
                }}
                class="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-accent transition-colors text-left group cursor-pointer"
              >
                <div class="h-9 w-9 rounded-full bg-primary/5 flex items-center justify-center text-primary/60 group-hover:scale-110 transition-transform">
                  <ArrowRight class="h-4 w-4" />
                </div>
                <div class="text-sm font-medium truncate">
                  {(() => {
                    const name = group.name;
                    return t(msg`Move to: ${name}`);
                  })()}
                </div>
              </button>
            </Show>
          )}
        </For>
      </div>

      <div class="p-4 bg-muted/20">
        <Button
          variant="ghost"
          class="w-full h-10 font-bold rounded-xl"
          onClick={() => {
            props.onOpenChange(false);
          }}
        >
          {t(msg`Cancel`)}
        </Button>
      </div>
    </Dialog>
  );
}
